import * as React from 'react';

import { CommentsDialog, ISectionCommentEntry } from '../CommentsDialog';
import {
    IMonthlyRecapModel,
    IReportSectionDetailModel,
    ITimelineReportInputModel,
} from '../../../../interfaces/Report/ITimelineReport';
import {
    MonthlyUpdateReportSectionList,
    PlaintiffsFirmDisplayMode,
    ReportCaseDetailsLastColumn,
    ReportStatusTypesEnum,
    SystemGeneratedDocumentsTypesEnum,
    TimelineReportSections,
    TimelineReportTypeEnum,
} from '../../../../utilities/Constants';

import { CollabModalProvider } from '../../collab-report/modals/CollabModalContext';
import CollabReportController from '../../collab-report/CollabReportController';
import Common from '../../../../stores/Common';
import { ILookupModel } from '../../../../interfaces/ILookup';
import { IValidation } from '../../../../interfaces/IError';
import { Loader } from '../../../../components/shared/Loader';
import { LocalRoutes } from '../../../../utilities/LocalRoutes';
import { ReportSettingsModal } from '../ReportSettingsModal';
import ValidateUtils from '../../../../shared/validations';
import { cloneDeep } from 'lodash';
import { useParams } from 'react-router-dom';
import { useZoneContext } from '../../../../contexts/useZoneContext';

const getDefaultReportInputModel = (reportGuid: string | undefined) => {
    const inputModel: ITimelineReportInputModel = {
        type: TimelineReportTypeEnum.Monthly,
        entityGuid: reportGuid,
        reportSectionDetails: [],
    };

    MonthlyUpdateReportSectionList.forEach((section: ILookupModel) => {
        const sectionDetail: IReportSectionDetailModel = {
            section: section.id,
            sectionName: section.name ?? '',
        };
        inputModel.reportSectionDetails!.push(sectionDetail);
    });

    inputModel.additionalSettings = {
        plaintiffsFirmDisplayMode: PlaintiffsFirmDisplayMode.Hierarchy,
        reportCaseDetailsLastColumn: ReportCaseDetailsLastColumn.ProductPremises,
        reportCaseDetailsLastColumnDisplayName: 'Product/Premises',
    };

    return inputModel;
};

export function MonthlyRecapReport() {
    const { guid: reportGuid } = useParams();
    const zoneContext = useZoneContext();

    const [data, setData] = React.useState<IMonthlyRecapModel>();
    const [inputModel, setInputModel] = React.useState<ITimelineReportInputModel>(
        getDefaultReportInputModel(reportGuid)
    );

    const [showSettingsModal, setShowSettingsModal] = React.useState<boolean>(false);
    const [commentsModal, setCommentsModal] = React.useState<{
        open: boolean;
        section: number;
    }>({ open: false, section: 0 });
    const [validation, setValidation] = React.useState<IValidation>({});
    const [shouldRunReport, setShouldRunReport] = React.useState<boolean>(false);
    const [isPending, setIsPending] = React.useState<boolean>(false);
    const [reportStatusId, setReportStatusId] = React.useState<number | undefined>();

    const getSectionsAfterSectionCheckedChange = (
        event: React.ChangeEvent<HTMLInputElement>,
        section: TimelineReportSections,
        sections: IReportSectionDetailModel[] = []
    ) => {
        const sectionsCopy = cloneDeep(sections);
        const isChecked = event.target.checked;
        const found = sectionsCopy.find((x) => x.section === section);

        if (found) {
            found.excludeInReport = !isChecked;
        } else if (isChecked) {
            sectionsCopy?.push({
                section,
                sectionName: section.toString(),
            });
        }
        return sectionsCopy?.filter((detail) => !detail.excludeInReport);
    };

    const handleCommentsDialogSaveClick = (fromDialog: ISectionCommentEntry[]) => {
        const reportSectionDetailsCopy = cloneDeep(inputModel.reportSectionDetails ?? []);

        reportSectionDetailsCopy?.forEach((details) => {
            const found = fromDialog.find(
                (searchDetail) => searchDetail.section === details.section
            );

            if (found) {
                details.comment = found.comment;
            }
        });

        const toAdd = fromDialog.filter((details) => {
            return !reportSectionDetailsCopy.map((fd) => fd.section).includes(details.section);
        });

        toAdd.forEach((item) => {
            const { section, comment } = item;
            reportSectionDetailsCopy.push({
                section,
                comment,
                excludeInReport: false,
            } as IReportSectionDetailModel);
        });

        setCommentsModal((prev) => ({ ...prev, open: false }));
        setInputModel((prev) => ({
            ...prev,
            reportSectionDetails: reportSectionDetailsCopy,
        }));
    };

    const handleInputModelChange = (newInputModel: any) => {
        setInputModel(newInputModel as ITimelineReportInputModel);
    };

    const handleShowSettingsModal = (show: boolean) => {
        setShowSettingsModal(show);
    };

    const handleShowCommentsModal = (show: boolean, section: number) => {
        setCommentsModal({ open: show, section });
    };

    const handleSettingModalSubmit = (model: ITimelineReportInputModel) => {
        setInputModel(model);
        setShouldRunReport(true);
    };

    const handleCollabReportLoaded = (data: IMonthlyRecapModel | undefined) => {
        setData(data);
    };

    const handleReportStatusChange = (status: ILookupModel) => {
        setReportStatusId(status.id);
    };

    const getReportData = async () => {
        const validation: IValidation = {};

        if (!inputModel.startDate || !inputModel.endDate) {
            validation.settings = ['Enter Date Range'];
            setValidation(validation);
            return;
        }

        if (Common.dateDiffInDays(inputModel.endDate, inputModel.startDate) < 0) {
            validation.settings = ['From Date must be before To Date'];
            setValidation(validation);
            return;
        }

        if ((inputModel.reportSectionDetails?.length ?? 0) === 0) {
            validation.settings = ['Select at least 1 section'];
            setValidation(validation);
            return;
        }

        if (
            inputModel.reportSectionDetails?.some((element) =>
                [
                    TimelineReportSections.RiskIssues,
                    TimelineReportSections.SignificantActivity,
                    TimelineReportSections.UpcomingTrialDates,
                    TimelineReportSections.TenderedCases,
                ].includes(element.section)
            ) &&
            (!inputModel.additionalSettings ||
                !inputModel.additionalSettings.reportCaseDetailsLastColumnDisplayName)
        ) {
            validation.settings = ['Case Details - Last Column Header Name is required'];
            setValidation(validation);
            return;
        }

        setValidation(validation);
        setIsPending(true);

        const response = await zoneContext.zoneApiClient.getTimelineReportData<IMonthlyRecapModel>(inputModel);
        if (response.httpResponse.status === 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            setValidation(ValidateUtils.parseErrors(response.errors, response.errorMessage));
            setIsPending(false);
            return;
        }
        if (response.payload) {
            setShowSettingsModal(false);
            setData(response.payload);
        }
        setIsPending(false);
    };

    React.useEffect(() => {
        if (!reportGuid) {
            setShowSettingsModal(true);
            setData(undefined);
        }
    }, [reportGuid]);

    React.useEffect(() => {
        if (shouldRunReport) {
            getReportData();
            setShouldRunReport(false);
            setShowSettingsModal(false);
        }
    }, [shouldRunReport]);

    const { reportSectionDetails, startDate, endDate, entityGuid, saveReport, ...options } =
        inputModel;

    const exportParameters = {
        reportType: {
            id: SystemGeneratedDocumentsTypesEnum.MonthlyRecap.Value,
        },
        options,
        reportSectionDetails,
        date: {
            startDate,
            endDate,
        },
        entityGuid,
        saveReport,
    };

    const isDraftStatus = reportStatusId === ReportStatusTypesEnum.Draft;

    return (
        <>
            {isPending && <Loader />}
            <CollabModalProvider>
                <CollabReportController<IMonthlyRecapModel, ITimelineReportInputModel>
                    collabReportUrl={LocalRoutes.MonthlyRecapCollab}
                    data={data}
                    exportSettings={{ parameters: exportParameters }}
                    getReportData={getReportData}
                    inputModel={inputModel}
                    onCollabReportLoaded={handleCollabReportLoaded}
                    onInputModelChange={handleInputModelChange}
                    onReportStatusChange={handleReportStatusChange}
                    onShowCommentsModal={handleShowCommentsModal}
                    onShowSettingsModal={handleShowSettingsModal}
                    pageTitle="Monthly Update"
                    reportTypeId={SystemGeneratedDocumentsTypesEnum.MonthlyRecap.Value}
                    sections={MonthlyUpdateReportSectionList}
                    timelineReportTypeId={TimelineReportTypeEnum.Monthly}
                />
            </CollabModalProvider>
            <ReportSettingsModal
                disabled={!!reportGuid && !isDraftStatus}
                defaultReportInputModel={getDefaultReportInputModel(reportGuid)}
                initialInputModel={inputModel}
                readonly={false}
                open={showSettingsModal}
                onHide={() => setShowSettingsModal(false)}
                getSectionsAfterSectionCheckedChange={getSectionsAfterSectionCheckedChange}
                onSubmit={handleSettingModalSubmit}
                reportGuid={reportGuid}
                sections={MonthlyUpdateReportSectionList}
                setValidation={setValidation}
                validation={validation}
            />
            <CommentsDialog
                defaultSection={commentsModal.section}
                disabled={!!reportGuid && !isDraftStatus}
                open={commentsModal.open}
                onSaveClick={handleCommentsDialogSaveClick}
                sectionCommentMap={
                    inputModel?.reportSectionDetails
                        ?.filter((detail) => !detail.excludeInReport)
                        .map((detail) => ({
                            section: detail.section,
                            comment: detail.comment,
                        })) ?? []
                }
                sections={MonthlyUpdateReportSectionList}
            />
        </>
    );
}
